/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/CardWorkbookSection.tsx
 * FILE_ID: UI-KANBAN-DRAWER-WORKBOOK-001
 * PURPOSE: The drawer's "Workbook" row — shows that a spreadsheet is
 *   attached to this card (or offers to add one) and opens the shared
 *   workbook modal.
 * WHY: ARC-3 follow-up (operator 2026-07-03): "I just made this in the
 *   forecast for payment run card.. yet nothing is in the drawer".
 *   The workbook existed but the drawer gave zero indication of it.
 * FLOW:
 *   card.document_kinds → attached? → [Open workbook] → onOpenWorkbook(card)
 * RELATED:
 *   - KanbanCardWorkbookModal (the editor this opens, via the board host)
 *   - CardTabContent.tsx (mount, next to the Entries section)
 */
import { Table2 } from 'lucide-react'
import type { Card } from '../../../types'
import { prefetchKanbanWorkbookIntent } from '../../../workbook/prefetchKanbanWorkbookIntent'

export function CardWorkbookSection({
  card,
  onOpenWorkbook,
}: {
  card: Card
  onOpenWorkbook: (card: Card) => void
}) {
  const hasWorkbook = (card.document_kinds ?? []).includes('workbook')
  const warm = () => prefetchKanbanWorkbookIntent()
  return (
    <section data-testid="card-workbook-section">
      <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50/60 px-3 py-2 dark:border-neutral-800 dark:bg-neutral-900/40">
        <div className="flex min-w-0 items-center gap-2">
          <Table2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div className="min-w-0">
            <div className="text-[12px] font-medium text-neutral-800 dark:text-neutral-100">
              Workbook
            </div>
            <div className="truncate text-[11px] text-neutral-500 dark:text-neutral-400">
              {hasWorkbook
                ? 'Spreadsheet attached to this card'
                : 'No sheet yet — add calculations to this card'}
            </div>
          </div>
        </div>
        <button
          type="button"
          onMouseEnter={warm}
          onFocus={warm}
          onClick={() => {
            warm()
            onOpenWorkbook(card)
          }}
          className="shrink-0 rounded-lg border border-neutral-200 px-2.5 py-1 text-[11px] font-medium text-neutral-700 hover:bg-white dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
        >
          {hasWorkbook ? 'Open workbook' : 'Add sheet'}
        </button>
      </div>
    </section>
  )
}
